import React, { useState, useEffect, useCallback } from 'react';
import {
  Plus,
  Copy,
  Trash2,
  Edit,
  X,
  MessageSquare,
  Lock,
  AlertCircle,
} from 'lucide-react';
import { channelService } from '../../../services/channelService';

const CHANNEL_CONFIGS = [
  {
    type:          'WHATSAPP',
    label:         'WhatsApp',
    webhookPath:   '/webhooks/whatsapp',
    idField:       'phoneNumberId',
    idLabel:       'Phone Number ID',
    idPlaceholder: '1234567890123456',
    idHint:        'Meta App → WhatsApp → API Setup → Phone Number ID',
    billingNote:   'WhatsApp charges may apply to your Meta account after the free tier (1,000 service conversations/month).',
  },
  {
    type:          'MESSENGER',
    label:         'Messenger',
    webhookPath:   '/webhooks/facebook',
    idField:       'pageId',
    idLabel:       'Facebook Page ID',
    idPlaceholder: '123456789012345',
    idHint:        'Your Facebook Page → About → Page ID',
    billingNote:   'Meta charges may apply for Messenger API usage beyond the free tier.',
  },
  {
    type:          'INSTAGRAM',
    label:         'Instagram',
    webhookPath:   '/webhooks/instagram',
    idField:       'pageId',
    idLabel:       'Instagram Account ID',
    idPlaceholder: '123456789012345',
    idHint:        'Your Instagram Business Account numeric ID (connected via Meta Business Suite)',
    billingNote:   'Meta charges may apply for Instagram Messaging API usage.',
  },
];

const EMPTY_FORM = {
  displayName:        '',
  phoneNumberId:      '',
  pageId:             '',
  accessToken:        '',
  appSecret:          '',
  webhookVerifyToken: '',
};

const ChatbotChannels = ({ chatbotId }) => {
  const [channels, setChannels]           = useState([]);
  const [loading, setLoading]             = useState(true);
  const [error, setError]                 = useState(null);
  const [activeType, setActiveType]       = useState('WHATSAPP');

  const [showModal, setShowModal]           = useState(false);
  const [editingChannel, setEditingChannel] = useState(null);
  const [formData, setFormData]             = useState(EMPTY_FORM);
  const [saving, setSaving]                 = useState(false);
  const [formError, setFormError]           = useState(null);
  const [copied, setCopied]                 = useState(false);

  const cfg = CHANNEL_CONFIGS.find(c => c.type === activeType);
  // REACT_APP_CHATBOT_WEBHOOK_BASE_URL is inlined at build time and is what the user
  // pastes into Meta's Callback URL field. If a deploy forgets it, fall back to the host
  // serving this portal — every portal host proxies /webhooks/ to the chatbot service —
  // rather than to an API URL that may point somewhere Meta cannot reach.
  const baseUrl = process.env.REACT_APP_CHATBOT_WEBHOOK_BASE_URL
                  || (typeof window !== 'undefined' ? window.location.origin : null)
                  || process.env.REACT_APP_API_URL
                  || 'http://localhost:8084';
  const webhookUrl      = baseUrl + cfg.webhookPath;
  const visibleChannels = channels.filter(ch => ch.channelType === activeType);

  const loadChannels = useCallback(async () => {
    if (!chatbotId) return;
    setLoading(true);
    setError(null);
    try {
      const data = await channelService.getChannels(chatbotId);
      setChannels(data || []);
    } catch (err) {
      setError(err.message || 'Failed to load channels');
    } finally {
      setLoading(false);
    }
  }, [chatbotId]);

  useEffect(() => { loadChannels(); }, [loadChannels]);

  const openAddModal = () => {
    setEditingChannel(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
    setShowModal(true);
  };

  const openEditModal = (channel) => {
    setEditingChannel(channel);
    setFormData({
      displayName:        channel.displayName || '',
      phoneNumberId:      channel.phoneNumberId || '',
      pageId:             channel.pageId || '',
      accessToken:        '',
      appSecret:          '',
      webhookVerifyToken: '',
    });
    setFormError(null);
    setShowModal(true);
  };

  const closeModal = () => {
    setShowModal(false);
    setEditingChannel(null);
    setFormData(EMPTY_FORM);
    setFormError(null);
  };

  const handleSave = async (e) => {
    e.preventDefault();
    if (!formData.displayName.trim()) { setFormError('Display Name is required.'); return; }

    const idValue = formData[cfg.idField].trim();
    if (!idValue) { setFormError(`${cfg.idLabel} is required.`); return; }
    if (!editingChannel && !formData.accessToken.trim()) { setFormError('Access Token is required.'); return; }
    if (!formData.webhookVerifyToken.trim()) { setFormError('Webhook Verify Token is required.'); return; }

    setSaving(true);
    setFormError(null);
    try {
      const payload = {
        channelType:        activeType,
        displayName:        formData.displayName,
        [cfg.idField]:      idValue,
        webhookVerifyToken: formData.webhookVerifyToken,
      };
      if (formData.accessToken.trim()) payload.accessToken = formData.accessToken;
      // Optional everywhere, and blank on edit means "keep the stored one" — same rule as the
      // access token. Sending '' would be read as "no change", never as "clear it".
      if (formData.appSecret.trim()) payload.appSecret = formData.appSecret;

      if (editingChannel) {
        const updated = await channelService.updateChannel(chatbotId, editingChannel.id, payload);
        setChannels(prev => prev.map(c => c.id === editingChannel.id ? updated : c));
      } else {
        const created = await channelService.createChannel(chatbotId, payload);
        setChannels(prev => [...prev, created]);
      }
      closeModal();
    } catch (err) {
      setFormError(err.message || 'Failed to save channel');
    } finally {
      setSaving(false);
    }
  };

  const handleToggle = async (channel) => {
    try {
      const updated = await channelService.toggleChannel(chatbotId, channel.id);
      setChannels(prev => prev.map(c => c.id === channel.id ? updated : c));
    } catch (err) {
      setError(err.message || 'Failed to toggle channel');
    }
  };

  const handleDelete = async (channelId) => {
    if (!window.confirm(`Delete this ${cfg.label} channel? This cannot be undone.`)) return;
    try {
      await channelService.deleteChannel(chatbotId, channelId);
      setChannels(prev => prev.filter(c => c.id !== channelId));
    } catch (err) {
      setError(err.message || 'Failed to delete channel');
    }
  };

  const handleCopyWebhook = () => {
    navigator.clipboard.writeText(webhookUrl).catch(() => {});
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleFieldChange = (field) => (e) => {
    setFormData(prev => ({ ...prev, [field]: e.target.value }));
  };

  return (
    <div className="mt-8 border-t pt-6">
      {/* Section header */}
      <div className="flex items-center justify-between mb-4">
        <h4 className="font-semibold text-gray-800 flex items-center gap-2">
          <MessageSquare className="w-4 h-4 text-indigo-500" />
          Messaging Channels
        </h4>
        <button
          onClick={openAddModal}
          className="flex items-center gap-1 text-sm bg-indigo-600 text-white px-3 py-1.5 rounded-lg hover:bg-indigo-700 transition"
        >
          <Plus className="w-3.5 h-3.5" />
          Add Channel
        </button>
      </div>

      {/* Tabs */}
      <div className="flex gap-1 mb-4 border-b border-gray-200">
        {CHANNEL_CONFIGS.map(c => (
          <button
            key={c.type}
            onClick={() => setActiveType(c.type)}
            className={`px-4 py-2 text-sm font-medium transition border-b-2 -mb-px ${
              activeType === c.type
                ? 'border-indigo-600 text-indigo-600'
                : 'border-transparent text-gray-500 hover:text-gray-700'
            }`}
          >
            {c.label}
          </button>
        ))}
      </div>

      {/* Webhook URL info box */}
      <div className="mb-4 flex items-start gap-3 bg-blue-50 border border-blue-200 rounded-lg px-4 py-3">
        <AlertCircle className="w-4 h-4 text-blue-600 flex-shrink-0 mt-0.5" />
        <div className="min-w-0 flex-1">
          <p className="text-xs font-medium text-blue-800 mb-1">Your {cfg.label} Webhook URL</p>
          <div className="flex items-center gap-2">
            <span className="text-xs font-mono text-blue-700 truncate">{webhookUrl}</span>
            <button
              onClick={handleCopyWebhook}
              className="flex-shrink-0 flex items-center gap-1 text-xs text-blue-600 hover:text-blue-800 transition"
              title="Copy webhook URL"
            >
              <Copy className="w-3.5 h-3.5" />
              {copied ? 'Copied!' : 'Copy'}
            </button>
          </div>
          <p className="text-xs text-yellow-700 bg-yellow-50 border border-yellow-200 rounded px-2 py-1 mt-2">
            {cfg.billingNote}
          </p>
        </div>
      </div>

      {/* Error banner */}
      {error && (
        <div className="mb-3 rounded-lg p-3 text-xs bg-red-50 border border-red-200 text-red-600">{error}</div>
      )}

      {loading && <p className="text-sm text-gray-400 text-center py-4">Loading channels…</p>}

      {!loading && visibleChannels.length === 0 && (
        <p className="text-sm text-gray-400 text-center py-4">
          No {cfg.label} channels configured. Add one to receive messages via {cfg.label}.
        </p>
      )}

      {/* Channel list */}
      {!loading && visibleChannels.map(channel => (
        <div
          key={channel.id}
          className={`mb-3 rounded-xl border p-4 ${
            channel.isEnabled ? 'border-green-200 bg-green-50' : 'border-gray-200 bg-gray-50'
          }`}
        >
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0 flex-1">
              <div className="flex items-center gap-2 mb-1">
                <span className="font-medium text-gray-800 text-sm">{channel.displayName}</span>
                <span className={`text-xs px-1.5 py-0.5 rounded-full font-medium ${
                  channel.isEnabled ? 'bg-green-100 text-green-700' : 'bg-gray-200 text-gray-500'
                }`}>
                  {channel.isEnabled ? 'Enabled' : 'Disabled'}
                </span>
              </div>
              <p className="text-xs text-gray-400 font-mono truncate">
                {cfg.idLabel}: {channel[cfg.idField]}
              </p>
            </div>
            <div className="flex items-center gap-1 flex-shrink-0">
              <button
                onClick={() => handleToggle(channel)}
                className={`relative inline-flex h-5 w-9 items-center rounded-full transition-colors ${
                  channel.isEnabled ? 'bg-green-500' : 'bg-gray-300'
                }`}
                title={channel.isEnabled ? 'Disable' : 'Enable'}
              >
                <span className={`inline-block h-3.5 w-3.5 transform rounded-full bg-white transition-transform ${
                  channel.isEnabled ? 'translate-x-4' : 'translate-x-1'
                }`} />
              </button>
              <button onClick={() => openEditModal(channel)} className="p-1.5 text-gray-400 hover:text-indigo-600 rounded" title="Edit">
                <Edit className="w-3.5 h-3.5" />
              </button>
              <button onClick={() => handleDelete(channel.id)} className="p-1.5 text-gray-400 hover:text-red-500 rounded" title="Delete">
                <Trash2 className="w-3.5 h-3.5" />
              </button>
            </div>
          </div>
        </div>
      ))}

      {/* Add / Edit Modal */}
      {showModal && (
        <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-2xl max-w-lg w-full">
            <div className="flex items-center justify-between p-6 border-b border-gray-200">
              <h2 className="text-xl font-bold text-gray-900">
                {editingChannel ? `Edit ${cfg.label} Channel` : `Add ${cfg.label} Channel`}
              </h2>
              <button onClick={closeModal} className="p-2 hover:bg-gray-100 rounded-lg">
                <X className="w-6 h-6 text-gray-500" />
              </button>
            </div>

            <form onSubmit={handleSave} className="p-6 space-y-4">
              {/* Display Name */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Display Name *</label>
                <input
                  type="text"
                  value={formData.displayName}
                  onChange={handleFieldChange('displayName')}
                  placeholder={`My ${cfg.label} Channel`}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* Dynamic ID field */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">{cfg.idLabel} *</label>
                <input
                  type="text"
                  value={formData[cfg.idField]}
                  onChange={handleFieldChange(cfg.idField)}
                  placeholder={cfg.idPlaceholder}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <p className="text-xs text-gray-400 mt-1">{cfg.idHint}</p>
              </div>

              {/* Access Token */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  Access Token {editingChannel ? <span className="font-normal text-gray-400">(leave blank to keep existing)</span> : '*'}
                </label>
                <input
                  type="password"
                  value={formData.accessToken}
                  onChange={handleFieldChange('accessToken')}
                  placeholder={editingChannel ? '••••••••' : 'EAAxxxxxxx…'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
              </div>

              {/* App Secret */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1 flex items-center gap-1">
                  <Lock className="w-3 h-3" />
                  App Secret {editingChannel ? <span className="font-normal text-gray-400">(leave blank to keep existing)</span> : ''}
                </label>
                <input
                  type="password"
                  value={formData.appSecret}
                  onChange={handleFieldChange('appSecret')}
                  placeholder={editingChannel && editingChannel.hasAppSecret ? '••••••••' : '32-character app secret'}
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Meta App Dashboard → Settings → Basic → App Secret. Used to verify that incoming
                  webhooks really came from Meta. Leave it empty and incoming webhooks cannot be
                  verified — anyone who knows your webhook URL could send messages as your bot.
                </p>
              </div>

              {/* Webhook Verify Token */}
              <div>
                <label className="block text-xs font-medium text-gray-600 mb-1">Webhook Verify Token *</label>
                <input
                  type="text"
                  value={formData.webhookVerifyToken}
                  onChange={handleFieldChange('webhookVerifyToken')}
                  placeholder="my_secret_verify_token"
                  className="w-full border border-gray-300 rounded-lg px-3 py-2 text-sm focus:outline-none focus:ring-2 focus:ring-indigo-400"
                />
                <p className="text-xs text-gray-400 mt-1">
                  Enter this same token in your Meta App dashboard under Webhook configuration.
                </p>
              </div>

              {formError && (
                <div className="rounded-lg p-3 text-xs bg-red-50 border border-red-200 text-red-600">{formError}</div>
              )}

              <div className="flex gap-2 pt-1">
                <button type="button" onClick={closeModal} className="flex-1 border border-gray-300 text-gray-600 text-sm py-2 rounded-lg hover:bg-gray-50 transition">
                  Cancel
                </button>
                <button type="submit" disabled={saving} className="flex-1 bg-indigo-600 text-white text-sm py-2 rounded-lg hover:bg-indigo-700 disabled:opacity-60 transition">
                  {saving ? 'Saving…' : editingChannel ? 'Save Changes' : 'Add Channel'}
                </button>
              </div>
            </form>
          </div>
        </div>
      )}
    </div>
  );
};

export default ChatbotChannels;
